/**
 * Wave chat-instant · ChatLauncher
 *
 * Botão da TopBar que abre/fecha o ChatPanel. SEPARADO visualmente do
 * InboxBell (email formal): ícone de balão de fala, badge azul-brand
 * em vez de vermelho-err, e clica → drawer lateral com lista de chats.
 *
 * Visual contract:
 *   - 44pt touch target (h-11 w-11, iOS HIG)
 *   - Mesma família visual dos outros TopBar controls (border, surface-2,
 *     hover:bg-surface-3, focus-ring, transition-colors 150ms)
 *   - Badge mostra unread total agregado (somatório de todas as convs)
 *   - Cap visual em "9+" e "99+" para não estourar layout
 *   - Sem animação no botão em si (ação repetida 100+ vezes/dia,
 *     guidance Emil); badge entra com fade-in subtle quando passa de 0
 *
 * Por que não reaproveitar InboxBell? Email = subject + retenção 30d +
 * audit. Chat = mensagem curta + 7d + emoji. Misturar confunde o PM
 * sobre velocidade esperada de resposta. JP foi explícito.
 */

import { useEffect } from 'react';
import { useAuth } from '@/store/auth.store';
import {
  useChatInstantStore,
  selectUnreadTotal,
} from '@/store/chat-instant.store';
import { chatInstantApi } from '@/api/chat-instant';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

// Polling do badge: 60s é suficiente pra manter o contador visível sem
// causar 429. O polling rápido (3s) acontece DENTRO da ChatWindow aberta,
// onde o usuário tá olhando. Wave 39.B · reduzido de 8s → 60s.
const BADGE_POLL_MS = 60_000;

export function ChatLauncher() {
  const { t } = useT();
  const user = useAuth((s) => s.user);
  const panelOpen = useChatInstantStore((s) => s.panelOpen);
  const togglePanel = useChatInstantStore((s) => s.togglePanel);
  const setConversations = useChatInstantStore((s) => s.setConversations);
  const unreadTotal = useChatInstantStore(selectUnreadTotal);

  // Background poll do unread badge. Reusa listConversations porque já
  // traz unreadCount por conv — evita endpoint dedicado.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = async () => {
      try {
        const list = await chatInstantApi.listConversations();
        if (alive) setConversations(list);
      } catch {
        // Offline / 401 / etc — silencioso. Próximo tick recupera.
      }
    };
    void tick();
    const id = window.setInterval(tick, BADGE_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [user, setConversations]);

  if (!user) return null;

  const badgeLabel =
    unreadTotal > 99 ? '99+' : unreadTotal > 9 ? '9+' : String(unreadTotal);

  const tooltip =
    unreadTotal > 0
      ? `${t.chatInstant.launcher.tooltip} · ${t.chatInstant.launcher.unreadSuffix(unreadTotal)}`
      : t.chatInstant.launcher.tooltip;

  return (
    <button
      type="button"
      data-testid="chat-instant-launcher"
      onClick={togglePanel}
      aria-haspopup="dialog"
      aria-expanded={panelOpen}
      aria-label={tooltip}
      title={tooltip}
      className={cn(
        'pressable relative flex h-11 w-11 items-center justify-center rounded-full border border-line',
        'bg-surface-2 text-ink-muted hover:text-ink hover:bg-surface-3 transition-colors duration-150',
        'focus-ring',
        panelOpen && 'bg-surface-3 text-ink',
      )}
    >
      {/* Speech bubble — afastado intencionalmente do envelope (InboxBell)
          pra que o PM diferencie no scan. */}
      <svg
        width="18"
        height="18"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M3 4.5C3 3.67 3.67 3 4.5 3h11C16.33 3 17 3.67 17 4.5v8c0 .83-.67 1.5-1.5 1.5H8.5l-3.4 3.4c-.31.32-.85.1-.85-.35V14H4.5C3.67 14 3 13.33 3 12.5v-8Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <circle cx="7" cy="8.5" r="0.9" fill="currentColor" />
        <circle cx="10" cy="8.5" r="0.9" fill="currentColor" />
        <circle cx="13" cy="8.5" r="0.9" fill="currentColor" />
      </svg>

      {unreadTotal > 0 && (
        <span
          data-testid="chat-instant-badge"
          className={cn(
            // Pinned inside the button to avoid layout bleed (same fix
            // applied to InboxBell em 2026-05-16). Brand-500 instead of
            // err-red — chat unread não é alerta, é convite.
            'absolute right-1 top-1 inline-flex h-[14px] min-w-[14px] items-center justify-center',
            'rounded-full bg-brand-500 px-[3px] text-[9px] font-semibold leading-none text-white',
            'animate-fade-in',
          )}
        >
          {badgeLabel}
        </span>
      )}
    </button>
  );
}
